import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import {
  Session,
  User,
} from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';
import { securityMonitor } from '@/utils/securityMonitor';
import { SignUpCredentials } from '@/types';
import { UserRole } from '@/types/user';
import { translateSupabaseError } from '@/utils/supabaseErrorTranslator';
import { useLanguage } from '@/context/LanguageContext';
import { getSiteDomain } from '@/utils/domainUtils';
import { isResetPasswordPath } from '@/utils/languageUtils';

interface AuthContextProps {
  user: User | null;
  session: Session | null;
  userRole: UserRole | null;
  loading: boolean;
  isRecoverySession: boolean;
  signUp: (credentials: SignUpCredentials) => Promise<{ success: boolean; error?: string }>;
  signIn: (email: string, password: string) => Promise<{ success: boolean; error?: string; errorCode?: string }>;
  signOut: () => Promise<{ error: any }>;
  resetPassword: (email: string) => Promise<{ data: any; error: any }>;
  isAuthenticated: boolean;
  isSessionValid: () => boolean;
  hasRole: (requiredRoles: UserRole[]) => boolean;
  refreshUserRole: () => Promise<void>;
  clearRecoverySession: () => void;
}

const AuthContext = createContext<AuthContextProps>({
  user: null,
  session: null,
  userRole: null,
  loading: true,
  isRecoverySession: false,
  signUp: async () => ({ success: false, error: 'not initialized' }),
  signIn: async () => ({ success: false, error: 'not initialized', errorCode: undefined }),
  signOut: async () => ({ error: 'not initialized' }),
  resetPassword: async () => ({ data: null, error: 'not initialized' }),
  isAuthenticated: false,
  isSessionValid: () => false,
  hasRole: () => false,
  refreshUserRole: async () => {},
  clearRecoverySession: () => {},
});

export const useAuth = () => useContext(AuthContext);

interface AuthProviderProps {
  children: React.ReactNode;
}

// Session validity cache to prevent redundant checks
interface SessionValidityCache {
  isValid: boolean;
  timestamp: number;
  expires_at: number;
}

let sessionValidityCache: SessionValidityCache | null = null;
const VALIDITY_CACHE_TTL = 30000; // 30 seconds

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const { t } = useLanguage();
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [userRole, setUserRole] = useState<UserRole | null>(null);
  const [loading, setLoading] = useState(true);
  const [isRecoverySession, setIsRecoverySession] = useState(false);

  // Detect if current session is a recovery session - Fixed to be more precise
  const detectRecoverySession = useCallback((session: Session | null): boolean => {
    if (!session) {
      return false;
    }

    // Check sessionStorage for persistent recovery state first
    const storedRecoveryState = sessionStorage.getItem('isRecoverySession');
    if (storedRecoveryState === 'true') {
      return true;
    }

    // Only set recovery state if we're actually on the reset-password route
    const currentPath = window.location.pathname;
    if (isResetPasswordPath(currentPath)) {
      // Store recovery state in sessionStorage
      sessionStorage.setItem('isRecoverySession', 'true');
      return true;
    }

    // For all other cases, this is not a recovery session
    return false;
  }, []);

  // Clear recovery session state
  const clearRecoverySession = useCallback(() => {
    sessionStorage.removeItem('isRecoverySession');
    setIsRecoverySession(false);
  }, []);

  // Enhanced session validity check with caching
  const isSessionValid = useCallback((): boolean => {
    if (!session) {
      sessionValidityCache = null;
      return false;
    }

    const now = Math.floor(Date.now() / 1000);
    
    // Check cache first
    if (sessionValidityCache && 
        sessionValidityCache.expires_at === session.expires_at &&
        (Date.now() - sessionValidityCache.timestamp) < VALIDITY_CACHE_TTL) {
      return sessionValidityCache.isValid;
    }

    // Perform validity check
    const isValid = session.expires_at ? session.expires_at > now : false;
    
    // Update cache
    sessionValidityCache = {
      isValid,
      timestamp: Date.now(),
      expires_at: session.expires_at || 0
    };

    // Only log if session is invalid or this is the first check after login
    if (!isValid || !sessionValidityCache.timestamp) {
      console.log(`AuthContext: Session validity check - Valid: ${isValid}, Expires at: ${session.expires_at}, Now: ${now}`);
      
      if (!isValid) {
        securityMonitor.logAuthEvent('session_expired', session.user?.id, session.user?.email, {
          sessionExpiry: session.expires_at,
          currentTime: now
        });
      }
    }
    
    return isValid;
  }, [session?.expires_at]);

  // Fetch user role from Supabase with improved error handling
  const refreshUserRole = useCallback(async () => {
    if (!session?.user) {
      setUserRole(null);
      return;
    }

    // Don't fetch role for recovery sessions
    if (isRecoverySession) {
      setUserRole(null);
      return;
    }

    try {
      const { data: role, error } = await supabase.rpc('get_user_role');
      
      if (error) {
        if (error.message?.includes('policy') || error.message?.includes('permission')) {
          console.log('AuthContext: RLS policy restricting role access - user may not exist in users table yet');
          setUserRole(null);
          return;
        }
        
        console.error('AuthContext: Error fetching user role:', error);
        securityMonitor.logErrorEvent(
          'Failed to fetch user role in AuthContext',
          'auth_context',
          session.user.id,
          { error: translateSupabaseError(error, t), code: error.code }
        );
        setUserRole(null);
        return;
      }

      const validRoles: UserRole[] = ['admin', 'user', 'ambassador', 'auditor', 'system'];
      
      if (!role || !validRoles.includes(role)) {
        console.log('AuthContext: Invalid or missing user role:', role);
        setUserRole(null);
        return;
      }

      console.log('AuthContext: User role fetched successfully:', role);
      setUserRole(role);
    } catch (error) {
      console.error('AuthContext: Role fetch error:', error);
      securityMonitor.logErrorEvent(
        'Role fetch failed in AuthContext',
        'auth_context',
        session?.user?.id,
        { error: translateSupabaseError(error, t) }
      );
      setUserRole(null);
    }
  }, [session?.user?.id, isRecoverySession, t]);

  // Check if user has required role
  const hasRole = useCallback((requiredRoles: UserRole[]): boolean => {
    if (!userRole || isRecoverySession) return false;
    return requiredRoles.includes(userRole);
  }, [userRole, isRecoverySession]);

  // Track last auth event to prevent duplicate logging
  const lastAuthEventRef = React.useRef<{ event: string; timestamp: number } | null>(null);

  useEffect(() => {
    console.log('AuthContext: Setting up auth state listener');
    
    // Set up auth state change listener
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        console.log(`AuthContext: Auth state change event: ${event}`, {
          timestamp: new Date().toISOString(),
          hasSession: !!session,
          userId: session?.user?.id || 'none',
          sessionExpiry: session?.expires_at ? new Date(session.expires_at * 1000).toISOString() : 'none'
        });

        // Clear recovery session state on normal sign-in to prevent false positives
        if (event === 'SIGNED_IN' && session && !isResetPasswordPath(window.location.pathname)) {
          clearRecoverySession();
        }

        // Detect recovery session state
        const isRecovery = detectRecoverySession(session);
        setIsRecoverySession(isRecovery);

        // Filter out duplicate events within a short time window
        const now = Date.now();
        const isDuplicateEvent = lastAuthEventRef.current && 
          lastAuthEventRef.current.event === event && 
          (now - lastAuthEventRef.current.timestamp) < 5000; // 5 second window

        if (!isDuplicateEvent) {
          lastAuthEventRef.current = { event, timestamp: now };
        }

        // Log only essential authentication events for security monitoring
        const userId = session?.user?.id;
        const userEmail = session?.user?.email;

        // Only log unique events, not token refresh duplicates
        if (!isDuplicateEvent) {
          switch (event) {
            case 'SIGNED_IN':
              console.log('AuthContext: Processing SIGNED_IN event');
              securityMonitor.logAuthEvent('login_success', userId, userEmail, {
                loginMethod: 'email_password',
                sessionExpiry: session?.expires_at,
                isRecoverySession: isRecovery
              });
              break;
            case 'SIGNED_OUT':
              console.log('AuthContext: Processing SIGNED_OUT event');
              securityMonitor.logAuthEvent('logout_success', userId, userEmail);
              // Clear recovery session state on sign out
              clearRecoverySession();
              break;
          }
        }

        console.log('AuthContext: Updating session and user state');
        setSession(session);
        setUser(session?.user ?? null);
        setLoading(false);
      }
    );

    // Get initial session
    console.log('AuthContext: Getting initial session');
    supabase.auth.getSession().then(async ({ data: { session }, error }) => {
      if (error) {
        console.error('AuthContext: Error getting initial session:', error);
        securityMonitor.logErrorEvent(
          'Failed to get initial session',
          'auth_context',
          undefined,
          { error: error.message }
        );
      }

      console.log('AuthContext: Initial session retrieved', {
        hasSession: !!session,
        userId: session?.user?.id || 'none',
        sessionExpiry: session?.expires_at ? new Date(session.expires_at * 1000).toISOString() : 'none'
      });

      // Clear recovery session state on normal session load if not on reset-password page
      if (session && !isResetPasswordPath(window.location.pathname)) {
        clearRecoverySession();
      }

      // Detect recovery session state for initial session
      const isRecovery = detectRecoverySession(session);
      setIsRecoverySession(isRecovery);

      setSession(session);
      setUser(session?.user ?? null);
      setLoading(false);
    });

    return () => {
      console.log('AuthContext: Cleaning up auth state listener');
      subscription.unsubscribe();
    };
  }, [detectRecoverySession, clearRecoverySession]);

  // Fetch user role only when user ID changes and not in recovery session
  useEffect(() => {
    const userId = user?.id;
    if (userId && !isRecoverySession) {
      // Small delay to allow user record to be created by trigger on first signup
      const timer = setTimeout(() => {
        refreshUserRole();
      }, 100);
      
      return () => clearTimeout(timer);
    } else {
      setUserRole(null);
    }
  }, [user?.id, isRecoverySession, refreshUserRole]);

  const signUp = async (credentials: SignUpCredentials) => {
    try {
      console.log('AuthContext: Starting sign up process for:', credentials.email);
      setLoading(true);

      const userMetadata = {
        username: credentials.username,
        first_name: credentials.firstName,
        last_name: credentials.lastName,
        date_of_birth: credentials.dateOfBirth,
        gender: credentials.gender,
        phone: credentials.phone,
        country: credentials.country,
        referral_code: credentials.referralCode,
        language: credentials.language,
        registration_domain: getSiteDomain(),
      };

      if (credentials.referralCode) {
        console.log('AuthContext: Including referral code in signup metadata:', credentials.referralCode);
      }

      if (credentials.language) {
        console.log('AuthContext: Including language in signup metadata:', credentials.language);
      }

      const redirectUrl = `${window.location.origin}/sign-in`;
      
      console.log('AuthContext: Calling Supabase signUp with redirect URL:', redirectUrl);
      const { data, error } = await supabase.auth.signUp({
        email: credentials.email,
        password: credentials.password,
        options: {
          emailRedirectTo: redirectUrl,
          data: userMetadata
        }
      });

      if (error) {
        console.error('AuthContext: Sign up error:', error);
        const translatedError = translateSupabaseError(error, t);
        securityMonitor.logAuthEvent('signup_failed', undefined, credentials.email, {
          error: translatedError
        });
        return { success: false, error: translatedError };
      }

      if (data.user) {
        console.log('AuthContext: User signed up successfully');
        securityMonitor.logAuthEvent('signup_success', data.user.id, credentials.email, {
          emailConfirmed: !!data.user.email_confirmed_at
        });
      }

      return { success: true };
    } catch (error) {
      console.error('AuthContext: Sign up process error:', error);
      const translatedError = translateSupabaseError(error, t);
      securityMonitor.logErrorEvent(
        'Sign up process failed',
        'auth_context',
        undefined,
        { 
          error: translatedError,
          email: credentials.email
        }
      );
      return { success: false, error: translatedError };
    } finally {
      setLoading(false);
    }
  };

  const signIn = async (email: string, password: string) => {
    try {
      console.log('AuthContext: Starting sign in process for:', email);
      setLoading(true);
      
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (error) {
        console.error('AuthContext: Sign in error:', error);
        const translatedError = translateSupabaseError(error, t);
        securityMonitor.logAuthEvent('login_failed', undefined, email, {
          error: translatedError
        });
        // Detect unconfirmed email specifically
        const errorCode = error.message === 'Email not confirmed' ? 'emailNotConfirmed' : undefined;
        return { success: false, error: translatedError, errorCode };
      }
        
      console.log('AuthContext: User signed in successfully');

      return { success: true };
    } catch (error) {
      console.error('AuthContext: Sign in process error:', error);
      const translatedError = translateSupabaseError(error, t);
      securityMonitor.logErrorEvent(
        'Sign in process failed',
        'auth_context',
        undefined,
        { 
          error: translatedError,
          email
        }
      );
      return { success: false, error: translatedError };
    } finally {
      setLoading(false);
    }
  };

  const signOut = async () => {
    try {
      console.log('AuthContext: Starting sign out process');
      const currentUserId = user?.id;
      const currentUserEmail = user?.email;
      
      // Clear all auth-related data from localStorage
      if (typeof window !== 'undefined') {
        const keysToRemove = [];
        for (let i = 0; i < localStorage.length; i++) {
          const key = localStorage.key(i);
          if (key && (key.startsWith('sb-') || key.includes('auth') || key.includes('session') || key.includes('code-verifier'))) {
            keysToRemove.push(key);
          }
        }
        keysToRemove.forEach(key => localStorage.removeItem(key));
        console.log('AuthContext: Cleared localStorage auth data:', keysToRemove);

        // Clear specific sessionStorage variables
        const sessionKeysToRemove = ['csrf_token', 'csrf_token_timestamp', 'userDisplayInfo', 'isRecoverySession'];
        sessionKeysToRemove.forEach(key => {
          if (sessionStorage.getItem(key)) {
            sessionStorage.removeItem(key);
            console.log(`AuthContext: Cleared sessionStorage key: ${key}`);
          }
        });
      }
      
      const { error } = await supabase.auth.signOut();

      if (error) {
        console.error('AuthContext: Sign out error:', error);
        securityMonitor.logErrorEvent(
          'Sign out failed',
          'auth_context',
          currentUserId,
          { error: error.message }
        );
        throw error;
      }

      // Clear all local state
      setSession(null);
      setUser(null);
      setUserRole(null);
      setIsRecoverySession(false);
      sessionValidityCache = null;

      console.log('AuthContext: User signed out successfully and all state cleared');
      
      return { error: null };
    } catch (error) {
      console.error('AuthContext: Sign out process error:', error);
      securityMonitor.logErrorEvent(
        'Sign out process failed',
        'auth_context',
        user?.id,
        { error: error instanceof Error ? error.message : 'Unknown error' }
      );
      return { error };
    }
  };

  const resetPassword = async (email: string) => {
    try {
      console.log('AuthContext: Starting password reset process for:', email);
      const redirectUrl = `${window.location.origin}/reset-password`;

      const { data, error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: redirectUrl,
      });

      if (error) {
        console.error('AuthContext: Password reset error:', error);
        const translatedError = translateSupabaseError(error, t);
        securityMonitor.logAuthEvent('password_reset_failed', undefined, email, {
          error: translatedError
        });
        throw new Error(translatedError);
      }

      console.log('AuthContext: Password reset email sent successfully');
      securityMonitor.logAuthEvent('password_reset_requested', undefined, email);

      return { data, error: null };
    } catch (error) {
      console.error('AuthContext: Password reset process error:', error);
      const translatedError = translateSupabaseError(error, t);
      securityMonitor.logErrorEvent(
        'Password reset process failed',
        'auth_context',
        undefined,
        { 
          error: translatedError,
          email
        }
      );
      return { data: null, error: { message: translatedError } };
    }
  };

  // Memoized authentication status - exclude recovery sessions
  const isAuthenticated = useMemo(() => {
    return !!user && !!session && isSessionValid() && !isRecoverySession;
  }, [user?.id, session?.expires_at, isSessionValid, isRecoverySession]);

  const value = useMemo(() => ({
    user,
    session,
    userRole,
    loading,
    isRecoverySession,
    signUp,
    signIn,
    signOut,
    resetPassword,
    isAuthenticated,
    isSessionValid,
    hasRole,
    refreshUserRole,
    clearRecoverySession,
  }), [user?.id, session?.expires_at, userRole, loading, isRecoverySession, isAuthenticated, isSessionValid, hasRole, refreshUserRole, clearRecoverySession]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
